import { useState, useRef } from 'react';
import React, { useEffect, Fragment } from 'react';
import { ArrowLeft, Save, X, Plus, Trash, Trash2 } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getMembership, createMembership, updateMembership } from '../../actions/membership';
import { connect } from 'react-redux';
import { getProductPackages } from '../../actions/productPackage';
import { setAlert } from '../../actions/alert';

const Membership = ({getMembership, createMembership, updateMembership, setAlert, membership, loading, productPackages, getProductPackages}) => {

    const [formData, setFormData] = useState({
        name: '',
        info: {
            benefits: [{title: '', tagline: ''}],
            description: '',
            faqs: [{title: '', description: ''}]
        },
        productPackage: ''
    });
  
    const [errors, setErrors] = useState({
        name: '',
        info: {
            benefits: [],
            description: '',
            faqs: []
        },
        productPackage: ''
    });

    const { id } = useParams();
    const navigate = useNavigate();

    // Fetch product packages on component mount
    useEffect(() => {
        document.title = 'Membership Details';
        if(!productPackages || productPackages.length === 0){
            getProductPackages('?packageType=membership');
        }
    }, []); // Empty dependency array - only run on mount

    // Handle membership data loading and form population
    useEffect(() => {
        if(id !== 'new'){
            if(!membership || (membership._id !== id) || loading){
                getMembership(id)
            }
        }
    }, [id]); // Only depend on id, not membership/loading to avoid unnecessary calls

    // Handle form population when membership data is loaded
    useEffect(() => {
        if(id !== 'new' && membership && membership._id === id){
            setFormData({
                ...formData,
                ...membership
            });
        }
    }, [membership, id]);
  
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Clear error when typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleInfoChange = (field, value) => {
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                [field]: value
            }
        });
        
        // Clear error when typing
        if (errors.info && errors.info[field]) {
            setErrors({
                ...errors,
                info: {
                    ...errors.info,
                    [field]: ''
                }
            });
        }
    };

    const handleBenefitChange = (index, field, value) => {
        const updatedBenefits = [...formData.info.benefits];
        updatedBenefits[index][field] = value;
        
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                benefits: updatedBenefits
            }
        });
        
        // Clear error for this index when typing
        if (errors.info && errors.info.benefits && errors.info.benefits[index]) {
            const updatedErrors = [...errors.info.benefits];
            updatedErrors[index] = '';
            setErrors({
                ...errors,
                info: {
                    ...errors.info,
                    benefits: updatedErrors
                }
            });
        }
    };

    const handleFaqChange = (index, field, value) => {
        const updatedFaqs = [...formData.info.faqs];
        updatedFaqs[index][field] = value;
        
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                faqs: updatedFaqs
            }
        });
        
        // Clear error for this index when typing
        if (errors.info && errors.info.faqs && errors.info.faqs[index]) {
            const updatedErrors = [...errors.info.faqs];
            updatedErrors[index] = '';
            setErrors({
                ...errors,
                info: {
                    ...errors.info,
                    faqs: updatedErrors
                }
            });
        }
    };

    const addBenefit = () => {
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                benefits: [...formData.info.benefits, {title: '', tagline: ''}]
            }
        });
    };

    const addFaq = () => {
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                faqs: [...formData.info.faqs, {title: '', description: ''}]
            }
        });
    };

    const removeBenefit = (index) => {
        const updatedBenefits = formData.info.benefits.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                benefits: updatedBenefits
            }
        });
        
        // Also remove any errors for this index
        if (errors.info && errors.info.benefits && errors.info.benefits.length > 0) {
            const updatedErrors = errors.info.benefits.filter((_, i) => i !== index);
            setErrors({
                ...errors,
                info: {
                    ...errors.info,
                    benefits: updatedErrors
                }
            });
        }
    };

    const removeFaq = (index) => {
        const updatedFaqs = formData.info.faqs.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            info: {
                ...formData.info,
                faqs: updatedFaqs
            }
        });
        
        // Also remove any errors for this index
        if (errors.info && errors.info.faqs && errors.info.faqs.length > 0) {
            const updatedErrors = errors.info.faqs.filter((_, i) => i !== index);
            setErrors({
                ...errors,
                info: {
                    ...errors.info,
                    faqs: updatedErrors
                }
            });
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (id === 'new') {
            // Create new membership
            const result = await createMembership(formData);
            if (result) {
                // Navigate to the created membership page
                navigate(`/inventory/v1.2/memberships/${result._id}`);
            }
        } else {
            // Update existing membership
            const dataToUpdate = {
                ...formData,
                _id: id // Ensure the membership ID is included
            };
            const result = await updateMembership(dataToUpdate);
            if (result) {
                // Membership updated successfully
                console.log('Membership updated successfully');
            }
        }
    };
  
    const handleReset = () => {
        setFormData({
            name: '',
            info: {
                benefits: [{title: '', tagline: ''}],
                description: '',
                faqs: [{title: '', description: ''}]
            },
            productPackage: ''
        });
        
        setErrors({
            name: '',
            info: {
                benefits: [],
                description: '',
                faqs: []
            },
            productPackage: ''
        });
    };

    return (
        <Fragment>
            <div className='container-fluid py-3'>
                {/* Header */}
                <div className='row mb-3'>
                    <div className='col-12'>
                        <div className='d-flex justify-content-between align-items-center'>
                            <Link className='btn btn-outline-primary btn-sm' to='/inventory/v1.2/memberships'>
                                <ArrowLeft size={16} className="me-2" />
                                Back to Memberships
                            </Link>
                            <h4 className='text-primary mb-0'>
                                {id === 'new' ? 'Create New Membership' : 'Edit Membership'}
                            </h4>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form id="membership-form" onSubmit={onSubmit}>
                    <div className='row'>
                        {/* Main Form Content */}
                        <div className='col-12'>
                            {/* Basic Information Card */}
                            <div className='card shadow-sm mb-3'>
                                <div className='card-header bg-primary text-white py-2'>
                                    <h6 className='mb-0'>
                                        <i className='fas fa-info-circle me-2'></i>
                                        Basic Information
                                    </h6>
                                </div>
                                <div className='card-body py-3'>
                                    <div className='row'>
                                        <div className='col-md-6 mb-2'>
                                            <label className='form-label fw-bold small'>
                                                Membership Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter membership name"
                                                required
                                            />
                                        </div>
                                        <div className='col-md-6 mb-2'>
                                            <label className='form-label fw-bold small'>Product Package</label>
                                            <select
                                                name="productPackage"
                                                value={formData.productPackage}
                                                onChange={handleChange}
                                                className="form-select"
                                            >
                                                <option value="">Select Product Package</option>
                                                {productPackages && productPackages.map((pkg) => (
                                                    <option key={pkg._id} value={pkg._id}>
                                                        {pkg.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Description Card */}
                            <div className='card shadow-sm mb-3'>
                                <div className='card-header bg-info text-white py-2'>
                                    <h6 className='mb-0'>
                                        <i className='fas fa-align-left me-2'></i>
                                        Description
                                    </h6>
                                </div>
                                <div className='card-body py-3'>
                                    <div className='mb-2'>
                                        <label className='form-label fw-bold small'>Description</label>
                                        <textarea
                                            rows="3"
                                            value={formData.info.description}
                                            onChange={(e) => handleInfoChange('description', e.target.value)}
                                            className="form-control"
                                            placeholder="Enter membership description..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Benefits Card */}
                            <div className='card shadow-sm mb-3'>
                                <div className='card-header bg-success text-white d-flex justify-content-between align-items-center py-2'>
                                    <h6 className='mb-0'>
                                        <i className='fas fa-gift me-2'></i>
                                        Benefits
                                    </h6>
                                    <button
                                        type="button"
                                        onClick={addBenefit}
                                        className="btn btn-light btn-sm"
                                    >
                                        <Plus size={14} className="me-1" />
                                        Add Benefit
                                    </button>
                                </div>
                                <div className='card-body py-3'>
                                    {formData.info.benefits.map((benefit, index) => (
                                        <div key={index} className="border rounded p-2 mb-2 bg-light">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="mb-0 text-success small">Benefit #{index + 1}</h6>
                                                {formData.info.benefits.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeBenefit(index)}
                                                        className="btn btn-sm btn-outline-danger"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label fw-bold small">Title</label>
                                                    <input
                                                        type="text"
                                                        value={benefit.title}
                                                        onChange={(e) => handleBenefitChange(index, 'title', e.target.value)}
                                                        className="form-control form-control-sm"
                                                        placeholder="Benefit title"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label fw-bold small">Tagline</label>
                                                    <input
                                                        type="text"
                                                        value={benefit.tagline}
                                                        onChange={(e) => handleBenefitChange(index, 'tagline', e.target.value)}
                                                        className="form-control form-control-sm"
                                                        placeholder="Benefit tagline"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* FAQs Card */}
                            <div className='card shadow-sm mb-3'>
                                <div className='card-header bg-warning text-dark d-flex justify-content-between align-items-center py-2'>
                                    <h6 className='mb-0'>
                                        <i className='fas fa-question-circle me-2'></i>
                                        FAQs
                                    </h6>
                                    <button
                                        type="button"
                                        onClick={addFaq}
                                        className="btn btn-dark btn-sm"
                                    >
                                        <Plus size={14} className="me-1" />
                                        Add FAQ
                                    </button>
                                </div>
                                <div className='card-body py-3'>
                                    {formData.info.faqs.map((faq, index) => (
                                        <div key={index} className="border rounded p-2 mb-2 bg-light">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="mb-0 text-warning small">FAQ #{index + 1}</h6>
                                                {formData.info.faqs.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFaq(index)}
                                                        className="btn btn-sm btn-outline-danger"
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4 mb-2">
                                                    <label className="form-label fw-bold small">Question</label>
                                                    <input
                                                        type="text"
                                                        value={faq.title}
                                                        onChange={(e) => handleFaqChange(index, 'title', e.target.value)}
                                                        className="form-control form-control-sm"
                                                        placeholder="Question"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-8 mb-2">
                                                    <label className="form-label fw-bold small">Answer</label>
                                                    <textarea
                                                        rows="2"
                                                        value={faq.description}
                                                        onChange={(e) => handleFaqChange(index, 'description', e.target.value)}
                                                        className="form-control form-control-sm"
                                                        placeholder="Answer"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                    </div>
                </form>

                {/* Actions Section - Bottom */}
                <div className='row mt-3'>
                    <div className='col-12'>
                        <div className='card shadow-sm'>
                            <div className='card-body text-center py-3'>
                                {id === 'new' && (
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="btn btn-outline-secondary btn-sm me-2"
                                    >
                                        <X size={14} className="me-1" />
                                        Reset
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    form="membership-form"
                                    className="btn btn-primary btn-sm"
                                >
                                    <Save size={14} className="me-1" />
                                    {id === 'new' ? 'Create Membership' : 'Update Membership'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

Membership.propTypes = {
    getMembership: PropTypes.func.isRequired,
    createMembership: PropTypes.func.isRequired,
    updateMembership: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    membership: PropTypes.object,
    getProductPackages: PropTypes.func.isRequired,
    productPackages: PropTypes.array
};

const mapStateToProps = state => ({
    loading: state.membership.loading,
    membership: state.membership.membership,
    productPackages: state.productPackage.packages
});

export default connect(mapStateToProps, { getMembership, createMembership, updateMembership, setAlert, getProductPackages })(Membership); 